import { useEffect, useRef, useState } from "react";
import { auth } from "../../firebase";

// ── Markdown inline renderer (bold, italic, inline code) ─────────────────────
function renderInline(text) {
  const parts = [];
  const regex = /(\*\*([^*\n]+)\*\*|\*([^*\n]+)\*|`([^`\n]+)`)/g;
  let last = 0, m;
  while ((m = regex.exec(text)) !== null) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    if (m[0].startsWith("**"))
      parts.push(<strong key={m.index} className="font-bold">{m[2]}</strong>);
    else if (m[0].startsWith("*"))
      parts.push(<em key={m.index} className="italic">{m[3]}</em>);
    else
      parts.push(
        <code key={m.index} className="bg-gray-200 dark:bg-gray-600 px-1 py-0.5 rounded text-xs font-mono">
          {m[4]}
        </code>
      );
    last = m.index + m[0].length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts.length ? parts : text;
}

// ── Full markdown block renderer ─────────────────────────────────────────────
function MarkdownContent({ content }) {
  const lines = content.split("\n");
  const elements = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];

    // Ordered list: collect consecutive numbered items
    if (/^\d+\.\s/.test(line)) {
      const items = [];
      while (i < lines.length && /^\d+\.\s/.test(lines[i])) {
        items.push(<li key={i} className="ml-1">{renderInline(lines[i].replace(/^\d+\.\s/, ""))}</li>);
        i++;
      }
      elements.push(<ol key={`ol-${i}`} className="list-decimal list-inside space-y-0.5 my-1 text-sm">{items}</ol>);
      continue;
    }

    // Unordered list: collect consecutive bullet items
    if (/^[-*•]\s/.test(line)) {
      const items = [];
      while (i < lines.length && /^[-*•]\s/.test(lines[i])) {
        items.push(<li key={i} className="ml-1">{renderInline(lines[i].replace(/^[-*•]\s/, ""))}</li>);
        i++;
      }
      elements.push(<ul key={`ul-${i}`} className="list-disc list-inside space-y-0.5 my-1 text-sm">{items}</ul>);
      continue;
    }

    // Headings
    if (line.startsWith("### "))
      elements.push(<p key={i} className="font-bold text-sm mt-2 mb-0.5">{renderInline(line.slice(4))}</p>);
    else if (line.startsWith("## "))
      elements.push(<p key={i} className="font-bold text-base mt-2 mb-0.5">{renderInline(line.slice(3))}</p>);
    else if (line.startsWith("# "))
      elements.push(<p key={i} className="font-extrabold text-base mt-2 mb-1">{renderInline(line.slice(2))}</p>);
    // Horizontal rule
    else if (/^---+$/.test(line.trim()))
      elements.push(<hr key={i} className="my-2 border-gray-300 dark:border-gray-600" />);
    // Empty line = small spacing
    else if (line.trim() === "")
      elements.push(<div key={i} className="h-1" />);
    // Regular paragraph
    else
      elements.push(<p key={i} className="text-sm leading-relaxed">{renderInline(line)}</p>);

    i++;
  }

  return <div className="space-y-0.5">{elements}</div>;
}

export default function AntonyModalContent({onClose}) {
  const [input, setInput] = useState("");
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showConfirmClose, setShowConfirmClose] = useState(false);
  const [isOpen, setIsOpen] = useState(true);
  const bottomRef = useRef(null);
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  
  const handleSend = async () => {
    if (!input.trim()) return;

    const newMessages = [...messages, { role: "user", content: input }];
    setMessages(newMessages);
    setInput("");
    setLoading(true);
    setError(null);

    
    try {
      const idToken = await auth.currentUser.getIdToken();
      const res = await fetch(`${backendUrl}/api/chat`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${idToken}`,
        },
        body: JSON.stringify({ messages: newMessages }),
      });
      const data = await res.json();
      if (res.ok) {
        setMessages([...newMessages, { role: "assistant", content: data.reply }]);
      } else {
        setError(data.error || "Error en la respuesta");
      }
    } catch (err) {
      setError("Error de conexión");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleClose = () => {
    setShowConfirmClose(true);
  };

  const confirmClose = () => {
    onClose(); 
    setShowConfirmClose(false); 
  };



  if (!isOpen) return null;

  return (
    <div
      className="
        fixed inset-0 flex items-center justify-center
        bg-black bg-opacity-50 backdrop-blur-sm
        p-4 z-50
      "
      aria-modal="true"
      role="dialog"
      aria-labelledby="antony-title"
    >
      {/* Modal principal */}
      <div
        className="
          bg-white dark:bg-gray-900 rounded-2xl shadow-2xl
          w-full max-w-md
          p-6
          transform transition-transform duration-300 ease-out
          scale-100
          animate-fadeInScale
          flex flex-col relative
        "
      >
        {/* Botón de cierre */}
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 text-gray-500 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400 text-xl font-bold"
          aria-label="Cerrar"
        >
          ×
        </button>

        <h2
          id="antony-title"
          className="text-2xl font-extrabold mb-4 text-gray-900 dark:text-gray-100 select-none"
        >
          Hola, soy <span className="text-blue-600 dark:text-blue-400">Antony</span> 🤖
        </h2>

        <div
          className="
            flex-1 mb-4 overflow-y-auto
            max-h-72
            border border-gray-300 dark:border-gray-700
            rounded-lg
            p-4
            bg-gray-50 dark:bg-gray-800
            scrollbar-thin scrollbar-thumb-blue-500 scrollbar-track-gray-200 dark:scrollbar-track-gray-700
            transition-colors
          "
        >
          {messages.length === 0 && (
            <p className="text-gray-500 dark:text-gray-400 italic select-none">
              Escribe tu pregunta para comenzar la conversación...
            </p>
          )}

          {messages.map((msg, i) => (
            <div
              key={i}
              className={`mb-3 p-3 rounded-lg
                ${msg.role === "user"
                  ? "bg-blue-100 text-blue-900 text-right dark:bg-blue-900 dark:text-blue-100 self-end max-w-[85%] whitespace-pre-wrap"
                  : "bg-gray-200 text-gray-900 dark:bg-gray-700 dark:text-gray-100 self-start max-w-[90%]"
                }
                animate-fadeIn
              `}
            >
              {msg.role === "assistant"
                ? <MarkdownContent content={msg.content} />
                : msg.content}
            </div>
          ))}
          <div ref={bottomRef} />
        </div>

        <div className="flex items-center gap-3">
          <input
            type="text"
            placeholder="Escribe tu pregunta..."
            className="
              flex-1
              p-3
              rounded-lg
              border border-gray-300 dark:border-gray-700
              bg-white dark:bg-gray-900
              text-gray-900 dark:text-gray-100
              placeholder-gray-400 dark:placeholder-gray-500
              focus:outline-none focus:ring-2 focus:ring-blue-500
              transition
            "
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && !loading && handleSend()}
            disabled={loading}
          />
          <button
            onClick={handleSend}
            disabled={loading}
            className="
              bg-blue-600
              text-white
              py-3 px-5
              rounded-lg
              font-semibold
              hover:bg-blue-700
              disabled:opacity-50 disabled:cursor-not-allowed
              transition
              select-none
            "
          >
            {loading ? "Pensando..." : "Enviar"}
          </button>
        </div>

        {error && (
          <p className="mt-3 text-center text-red-600 dark:text-red-400 font-medium select-none">
            {error}
          </p>
        )}
      </div>

      {/* Modal de confirmación */}
      {showConfirmClose && (
        <div
          className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-60 z-50"
          aria-modal="true"
        >
          <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-lg w-full max-w-sm text-center animate-fadeInScale">
            <p className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
              ¿Estás seguro de que deseas cerrar esta conversación?
            </p>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
              La conversación actual se perderá y no será guardada.
            </p>
            <div className="flex justify-center gap-4">
              <button
                onClick={confirmClose}
                className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition"
              >
                Sí, cerrar
              </button>
              <button
                onClick={() => setShowConfirmClose(false)}
                className="bg-gray-300 dark:bg-gray-700 text-gray-900 dark:text-gray-100 px-4 py-2 rounded-lg hover:bg-gray-400 dark:hover:bg-gray-600 transition"
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Animaciones CSS */}
      <style>{`
        @keyframes fadeInScale {
          0% {
            opacity: 0;
            transform: scale(0.9);
          }
          100% {
            opacity: 1;
            transform: scale(1);
          }
        }
        @keyframes fadeIn {
          0% {
            opacity: 0;
          }
          100% {
            opacity: 1;
          }
        }
        .animate-fadeInScale {
          animation: fadeInScale 0.3s ease forwards;
        }
        .animate-fadeIn {
          animation: fadeIn 0.25s ease forwards;
        }
      `}</style>
    </div>
  );
}
